import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  FolderInput,
  FolderPlus,
  Library,
  Loader2,
  Sparkles,
  Users,
} from 'lucide-react';
import { ResourceType, type OrganizeLibraryMode } from '@cotera/api';
import { ModalPageView } from '@cotera/client/app/components/app';
import { useModalManager } from '@cotera/client/app/components/app/modal-manager/modal-manager';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@cotera/client/app/components/ui';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Checkbox } from '@cotera/client/v0/components/ui/checkbox';
import type { Result } from 'neverthrow';
import type {
  OrganizeDraft,
  OrganizeDraftFolder,
  OrganizeDraftResponse,
  OrganizeProposal,
  TenantedClient,
} from '@cotera/client/app/etc/api/typed-client';
import {
  DRAG_FOLDER_KEY,
  ProposalCard,
  parseMemberKey,
} from './organize-proposal-card';

/**
 * The organize review inside the Coco artifact panel, bound to the SHARED
 * per-conversation draft (server-held, staging-only — nothing is real until
 * "Move"). Both this panel and Coco's draft tools read/write that same draft,
 * so edits from either side show up on the other: the panel polls the draft and
 * applies each of its own edits optimistically from the API's returned draft.
 */

/**
 * Shared so the Library's Organize button can PREFETCH the draft at click —
 * starting the clustering before the chat even navigates — and this panel reads
 * the warm cache on mount instead of showing "Looking for things to organize".
 */
export const organizeDraftQueryKey = (conversationId: string) =>
  ['organize', 'draft', conversationId] as const;

/** The scope banner shown atop the plan, so which library is being organized (and
 * who the changes affect) is always labelled. One row, driven by the draft scope. */
const SCOPE_BANNER = {
  org: {
    Icon: Users,
    title: 'Shared library',
    caption: 'changes apply to everyone in your organization',
  },
  user: {
    Icon: Library,
    title: 'My library',
    caption: 'changes apply only to you',
  },
} as const;

const ScopeBanner: React.FC<{ scope: 'user' | 'org' }> = ({ scope }) => {
  const { Icon, title, caption } = SCOPE_BANNER[scope];
  return (
    <div className="mb-3 flex items-center gap-1.5 text-sm text-muted-foreground">
      <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
      <p>
        <span className="font-medium text-foreground">{title}</span> · {caption}
      </p>
    </div>
  );
};

/**
 * The panel's poll: read the draft, seeding one only when none exists. With no
 * `mode` the server keeps the draft's existing library (personal when there is
 * none), so the panel never has to guess one.
 */
export async function fetchOrCreateOrganizeDraft(
  client: TenantedClient,
  conversationId: string,
  mode?: OrganizeLibraryMode
): Promise<OrganizeDraft | null> {
  const got = await client.resource.folder.getOrganizeDraft({ conversationId });
  if (got.isOk() && got.value.draft !== null) {
    return got.value.draft;
  }
  const created = await client.resource.folder.createOrganizeDraft({
    conversationId,
    ...(mode !== undefined && { mode }),
  });
  return created.isOk() ? created.value.draft : null;
}

/**
 * The OPEN path (Library Organize button). Because we reuse an existing organize
 * chat, a stale empty draft from an earlier open would otherwise stick and always
 * report "all organized". So on an explicit open we RE-CLUSTER when the current
 * draft is empty (or absent), and only resume when there is an in-progress plan
 * (folders already staged). The panel's read-only polling keeps using
 * {@link fetchOrCreateOrganizeDraft}, so this re-cluster happens once per open,
 * never on every poll.
 */
export async function openOrganizeDraft(
  client: TenantedClient,
  conversationId: string,
  mode: 'personal' | 'shared' = 'personal'
): Promise<OrganizeDraft | null> {
  const got = await client.resource.folder.getOrganizeDraft({ conversationId });
  if (got.isOk() && (got.value.draft?.folders.length ?? 0) > 0) {
    return got.value.draft;
  }
  const created = await client.resource.folder.createOrganizeDraft({
    conversationId,
    mode,
  });
  return created.isOk() ? created.value.draft : null;
}

/**
 * Group folders by their parent's draftId (null = top level), so the panel can
 * render the plan as a real tree with subfolders nested under their parent. A
 * parentDraftId pointing at a missing folder (or itself) is treated as
 * top-level; folders stuck in a cycle are rescued at top level by the renderer
 * so nothing silently disappears from the review.
 */
function buildChildrenByParent(
  folders: OrganizeDraftFolder[]
): Map<string | null, OrganizeDraftFolder[]> {
  const byId = new Map(folders.map((f) => [f.draftId, f]));
  const childrenByParent = new Map<string | null, OrganizeDraftFolder[]>();
  for (const folder of folders) {
    const rawParent = folder.parentDraftId ?? null;
    const parent =
      rawParent !== null && rawParent !== folder.draftId && byId.has(rawParent)
        ? rawParent
        : null;
    const siblings = childrenByParent.get(parent) ?? [];
    siblings.push(folder);
    childrenByParent.set(parent, siblings);
  }
  return childrenByParent;
}

function proposalOf(folder: OrganizeDraftFolder): OrganizeProposal {
  return {
    groupId: folder.draftId,
    suggestedFolderName: folder.name,
    reason: '',
    existingFolderId: folder.existingFolderId,
    existingFolderName: folder.existingFolderName,
    members: folder.members,
  };
}

export const LibraryOrganizeDraftPanel: React.FC<{
  /** The chat/conversation id — the draft key shared with Coco's tools. */
  conversationId: string;
  /**
   * The library this chat was opened for, from the artifact payload. Undefined
   * for older chats and Coco-opened panels, where the draft's scope decides.
   */
  mode?: OrganizeLibraryMode;
  onApplied?: () => void;
}> = ({ conversationId, mode, onApplied }) => {
  const client = useTenantedClient();
  const org = useWhoami((s) => s.org);
  const queryClient = useQueryClient();
  const [applying, setApplying] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  /**
   * `updatedAt` of the draft a re-scan produced when it came back EMPTY. Only
   * then is "you're all organized" actually true; an empty plan the user (or
   * Coco) cleared by hand just means nothing is staged. Keyed to the draft
   * version so any later edit drops back to the neutral empty state.
   */
  const [emptyRescanAt, setEmptyRescanAt] = useState<number | null>(null);
  /** draftId -> true when the user unchecked a folder (excluded from Move). */
  const [deselectedFolders, setDeselectedFolders] = useState<
    Record<string, boolean>
  >({});

  const isSelected = useCallback(
    (draftId: string) => deselectedFolders[draftId] !== true,
    [deselectedFolders]
  );
  const toggleFolder = useCallback((draftId: string, value: boolean) => {
    setDeselectedFolders((prev) => {
      const next = { ...prev };
      if (value) {
        delete next[draftId];
      } else {
        next[draftId] = true;
      }
      return next;
    });
  }, []);
  const toggleAll = useCallback((value: boolean, allDraftIds: string[]) => {
    setDeselectedFolders(
      value ? {} : Object.fromEntries(allDraftIds.map((id) => [id, true]))
    );
  }, []);

  // Which library this chat organizes. Seeded from the payload, then kept in
  // step with the draft and remembered past a commit (which deletes the draft
  // row). Undefined when neither says (older chats, Coco-opened panels): then
  // no mode is sent and the server decides, so the panel never guesses one.
  const modeRef = useRef<OrganizeLibraryMode | undefined>(mode);

  const { data: draft, isLoading } = useQuery({
    queryKey: organizeDraftQueryKey(conversationId),
    enabled: conversationId !== '',
    // Poll so Coco's staged changes appear here, and vice versa.
    refetchInterval: 2500,
    refetchOnWindowFocus: true,
    queryFn: () =>
      fetchOrCreateOrganizeDraft(client, conversationId, modeRef.current),
  });

  // Run a draft edit, writing the returned draft authoritatively. First cancel
  // any in-flight poll for this key, so a refetch that started pre-edit can't
  // resolve afterward and revert the change for up to a poll interval.
  const runDraftMutation = useCallback(
    async (
      call: () => Promise<Result<OrganizeDraftResponse, unknown>>
    ): Promise<void> => {
      await queryClient.cancelQueries({
        queryKey: organizeDraftQueryKey(conversationId),
      });
      const result = await call();
      if (result.isOk()) {
        queryClient.setQueryData(
          organizeDraftQueryKey(conversationId),
          result.value.draft
        );
      }
    },
    [queryClient, conversationId]
  );

  useEffect(() => {
    if (draft !== undefined && draft !== null) {
      modeRef.current = draft.scope === 'org' ? 'shared' : 'personal';
    }
  }, [draft]);

  // Re-cluster the library into a fresh plan, replacing the current (empty)
  // one — the same seed the Library's Organize button runs, without making the
  // user leave the chat or archive it to start over.
  const handleRescan = useCallback(async () => {
    setRescanning(true);
    await queryClient.cancelQueries({
      queryKey: organizeDraftQueryKey(conversationId),
    });
    const r = await client.resource.folder.createOrganizeDraft({
      conversationId,
      ...(modeRef.current !== undefined && { mode: modeRef.current }),
    });
    setRescanning(false);
    if (r.isErr()) {
      toast.error("We couldn't look for new suggestions.");
      return;
    }
    const next = r.value.draft;
    setDeselectedFolders({});
    setEmptyRescanAt(
      next !== null && next.folders.length === 0 ? next.updatedAt : null
    );
    queryClient.setQueryData(organizeDraftQueryKey(conversationId), next);
  }, [client, conversationId, queryClient]);

  const folders = useMemo(() => draft?.folders ?? [], [draft]);
  const childrenByParent = useMemo(
    () => buildChildrenByParent(folders),
    [folders]
  );

  const handleRename = useCallback(
    (draftId: string, name: string) =>
      runDraftMutation(() =>
        client.resource.folder.renameOrganizeDraftFolder({
          conversationId,
          draftId,
          name,
        })
      ),
    [client, conversationId, runDraftMutation]
  );

  const handleDismiss = useCallback(
    (type: ResourceType, id: string) =>
      runDraftMutation(() =>
        client.resource.folder.removeOrganizeDraftMember({
          conversationId,
          member: { type, id },
        })
      ),
    [client, conversationId, runDraftMutation]
  );

  const handleDrop = useCallback(
    (toDraftId: string, key: string) => {
      const member = parseMemberKey(key);
      if (member === null) {
        return;
      }
      void runDraftMutation(() =>
        client.resource.folder.moveOrganizeDraftMember({
          conversationId,
          member,
          toDraftId,
        })
      );
    },
    [client, conversationId, runDraftMutation]
  );

  const handleAddFolder = useCallback(
    () =>
      runDraftMutation(() =>
        client.resource.folder.addOrganizeDraftFolder({
          conversationId,
          name: 'New folder',
        })
      ),
    [client, conversationId, runDraftMutation]
  );

  const handleDeleteFolder = useCallback(
    (draftId: string) =>
      runDraftMutation(() =>
        client.resource.folder.removeOrganizeDraftFolder({
          conversationId,
          draftId,
        })
      ),
    [client, conversationId, runDraftMutation]
  );

  // Drag-to-rearrange: nest a folder under another (parentDraftId) or move it
  // back to the top level (null). We guard against cycles client-side (the
  // server guards too) so dropping a folder onto itself or its own descendant is
  // a no-op instead of a wasted round-trip.
  const handleMoveFolder = useCallback(
    (draftId: string, parentDraftId: string | null) => {
      if (draftId === parentDraftId) {
        return;
      }
      if (parentDraftId !== null) {
        const byId = new Map(folders.map((f) => [f.draftId, f]));
        let cursor: string | null = parentDraftId;
        const seen = new Set<string>();
        while (cursor !== null && !seen.has(cursor)) {
          if (cursor === draftId) {
            return; // would nest a folder under its own descendant
          }
          seen.add(cursor);
          cursor = byId.get(cursor)?.parentDraftId ?? null;
        }
      }
      void runDraftMutation(() =>
        client.resource.folder.moveOrganizeDraftFolder({
          conversationId,
          draftId,
          parentDraftId,
        })
      );
    },
    [client, conversationId, runDraftMutation, folders]
  );

  const handleMove = useCallback(async () => {
    setApplying(true);
    // Folders that will actually commit, so we invalidate exactly the resource
    // lists whose contents change (one per moved type) — the commit response
    // only carries counts, not which types moved.
    const committing = (draft?.folders ?? []).filter(
      (f) => deselectedFolders[f.draftId] !== true && f.members.length > 0
    );
    const movedTypes = new Set<ResourceType>(
      committing.flatMap((f) => f.members.map((m) => m.type))
    );
    const r = await client.resource.folder.commitOrganizeDraft({
      conversationId,
      excludeDraftIds: (draft?.folders ?? [])
        .filter((f) => deselectedFolders[f.draftId] === true)
        .map((f) => f.draftId),
    });
    setApplying(false);
    if (r.isErr()) {
      toast.error("We couldn't apply those changes.");
      return;
    }
    const { folders: foldersTouched, items } = r.value;
    if (items === 0) {
      toast.error('There was nothing to move.');
      return;
    }
    toast.success(
      `Organized ${items} ${
        items === 1 ? 'item' : 'items'
      } into ${foldersTouched} ${foldersTouched === 1 ? 'folder' : 'folders'}.`
    );
    for (const type of movedTypes) {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.resource.list({ orgId: org.id, type, limit: 50 }),
      });
    }
    void queryClient.invalidateQueries({
      queryKey: queryKeys.resource.folderTree({ orgId: org.id }),
    });
    void queryClient.invalidateQueries({
      queryKey: queryKeys.resource.folderList({ orgId: org.id }),
    });
    queryClient.setQueryData(organizeDraftQueryKey(conversationId), null);
    onApplied?.();
  }, [
    client,
    conversationId,
    queryClient,
    org.id,
    onApplied,
    draft,
    deselectedFolders,
  ]);

  // Only claim "all organized" right after a re-scan found nothing to group.
  const confirmedOrganized =
    emptyRescanAt !== null && draft?.updatedAt === emptyRescanAt;

  const nonEmptySelected = folders.filter(
    (f) => f.members.length > 0 && isSelected(f.draftId)
  );
  const allSelected =
    folders.length > 0 && folders.every((f) => isSelected(f.draftId));
  const selectedItemCount = nonEmptySelected.reduce(
    (sum, f) => sum + f.members.length,
    0
  );
  const itemsLabel = `${selectedItemCount} ${
    selectedItemCount === 1 ? 'item' : 'items'
  }`;

  // The draft's scope is authoritative; before it loads (or after a commit
  // clears it) fall back to the library the chat was opened for, so the banner
  // is there while suggestions load and in the empty state too.
  const scope: 'user' | 'org' | null =
    draft !== undefined && draft !== null
      ? draft.scope === 'org'
        ? 'org'
        : 'user'
      : mode === 'shared'
      ? 'org'
      : mode === 'personal'
      ? 'user'
      : null;

  // Moving in the shared library rearranges it for the whole org, so it gets a
  // confirm step; a personal Move only affects the actor and stays one click.
  const modalManager = useModalManager((s) => s.actions);
  const requestMove = useCallback(() => {
    if (scope !== 'org') {
      void handleMove();
      return;
    }
    modalManager.push({
      view: () => (
        <ModalPageView
          title={`Move ${itemsLabel} in the shared library?`}
          subtitle="This rearranges the shared library for everyone in your organization."
          className="px-4 py-4"
        >
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => modalManager.pop()}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                modalManager.pop();
                void handleMove();
              }}
            >
              Move {itemsLabel}
            </Button>
          </div>
        </ModalPageView>
      ),
    });
  }, [scope, handleMove, modalManager, itemsLabel]);

  const moveButton = (
    <Button
      size="sm"
      className="gap-1.5"
      disabled={applying || nonEmptySelected.length === 0}
      onClick={requestMove}
    >
      {applying ? (
        <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
      ) : (
        <FolderInput className="h-4 w-4" aria-hidden="true" />
      )}
      {selectedItemCount > 0 ? `Move ${itemsLabel}` : 'Move'}
    </Button>
  );

  return (
    <div className="flex h-full w-full flex-col">
      <div
        className="flex-1 overflow-y-auto px-4 pb-4 pt-3"
        // Dropping a folder on empty panel space (a card's own drop stops
        // propagation) moves it back to the top level.
        onDragOver={(e) => {
          if (e.dataTransfer.types.includes(DRAG_FOLDER_KEY)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
          }
        }}
        onDrop={(e) => {
          if (!e.dataTransfer.types.includes(DRAG_FOLDER_KEY)) {
            return;
          }
          const droppedDraftId = e.dataTransfer.getData(DRAG_FOLDER_KEY);
          if (droppedDraftId !== '') {
            e.preventDefault();
            handleMoveFolder(droppedDraftId, null);
          }
        }}
      >
        {scope !== null ? <ScopeBanner scope={scope} /> : null}
        {isLoading || draft === undefined ? (
          <div className="flex items-center justify-center gap-2 py-20 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            Looking for things to organize…
          </div>
        ) : folders.length === 0 ? (
          <div className="flex flex-col items-center gap-4 py-20 text-center">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-foreground">
                {confirmedOrganized
                  ? 'You’re all organized.'
                  : 'No folders in this plan.'}
              </p>
              <p className="text-sm text-muted-foreground">
                {confirmedOrganized
                  ? 'We didn’t find anything new to group.'
                  : 'Get fresh suggestions, add a folder yourself, or ask Coco.'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                className="gap-1.5"
                disabled={rescanning}
                onClick={() => void handleRescan()}
              >
                {rescanning ? (
                  <Loader2
                    className="h-4 w-4 animate-spin"
                    aria-hidden="true"
                  />
                ) : (
                  <Sparkles className="h-4 w-4" aria-hidden="true" />
                )}
                {rescanning ? 'Suggesting folders…' : 'Suggest folders'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="gap-1.5 text-muted-foreground hover:text-foreground"
                disabled={rescanning}
                onClick={() => void handleAddFolder()}
              >
                <FolderPlus className="h-4 w-4" aria-hidden="true" />
                New folder
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div className="mb-3 flex items-center gap-3 pb-3">
              <Checkbox
                checked={allSelected}
                onCheckedChange={(value) =>
                  toggleAll(
                    value === true,
                    folders.map((f) => f.draftId)
                  )
                }
                aria-label="Select all folders"
              />
              <span className="text-sm text-muted-foreground">
                {folders.length} {folders.length === 1 ? 'folder' : 'folders'}
              </span>
              <span className="min-w-0 flex-1" aria-hidden="true" />
              <Button
                variant="ghost"
                size="sm"
                className="shrink-0 gap-1.5 text-muted-foreground hover:text-foreground"
                onClick={() => void handleAddFolder()}
              >
                <FolderPlus className="h-4 w-4" aria-hidden="true" />
                New folder
              </Button>
            </div>

            <div className="flex flex-col gap-3">
              {(() => {
                // Render the plan as a real tree: a folder's subfolders live
                // INSIDE its own expandable area (so opening a folder reveals its
                // subfolders), with a single `border-l` guide running down the
                // whole group. `visited` guards against cycles.
                const visited = new Set<string>();
                const renderNode = (
                  folder: OrganizeDraftFolder
                ): React.ReactNode => {
                  if (visited.has(folder.draftId)) {
                    return null;
                  }
                  visited.add(folder.draftId);
                  const children = childrenByParent.get(folder.draftId) ?? [];
                  return (
                    <ProposalCard
                      key={folder.draftId}
                      proposal={proposalOf(folder)}
                      members={folder.members}
                      folderName={folder.name}
                      onFolderNameChange={(name) =>
                        void handleRename(folder.draftId, name)
                      }
                      selected={isSelected(folder.draftId)}
                      onToggleFolder={(value) =>
                        toggleFolder(folder.draftId, value)
                      }
                      onDismissMember={(type, id) =>
                        void handleDismiss(type, id)
                      }
                      onDropMember={(key) =>
                        void handleDrop(folder.draftId, key)
                      }
                      onDropFolder={(droppedDraftId) =>
                        handleMoveFolder(droppedDraftId, folder.draftId)
                      }
                      onDeleteFolder={() =>
                        void handleDeleteFolder(folder.draftId)
                      }
                      subfolderCount={children.length}
                      subfolders={
                        children.length > 0 ? (
                          <div className="mt-1.5 flex flex-col gap-2 border-l border-border pl-3">
                            {children.map((child) => renderNode(child))}
                          </div>
                        ) : undefined
                      }
                    />
                  );
                };
                const roots = childrenByParent.get(null) ?? [];
                return [
                  ...roots.map((folder) => renderNode(folder)),
                  // Rescue any folder trapped in a cycle (unreachable from a root)
                  // so it still appears in the review.
                  ...folders
                    .filter((folder) => !visited.has(folder.draftId))
                    .map((folder) => renderNode(folder)),
                ];
              })()}
            </div>
          </>
        )}
      </div>

      {/* Move is the final confirm, so it sits in a pinned footer below the
          scrollable list rather than above "New folder". */}
      {/* h-20 matches the sidebar account card's height so this bar's top border
          lines up with the sidebar's separator above the account card. */}
      <div className="flex h-20 shrink-0 items-center justify-end gap-3 border-t border-border/60 bg-card px-4">
        {moveButton}
      </div>
    </div>
  );
};
